// Achievement manager. Subscribes to the game's lifecycle events, maintains
// per-event lifetime counters on the profile, and awards milestone badges
// when triggers cross their threshold. Each unlock emits NarrativeBeat
// (for the celebratory modal) and AchievementUnlocked (for any HUD reactions).
//
// Triggers (see AchievementConfig in balance.js):
//   • count   — sum of an event hitting threshold
//   • profile — a profile-field crossing threshold (numeric or boolean)
//   • kingdom — an active-kingdom-field crossing threshold
//   • activeMarriages — number of ACTIVE marriages
//   • membership — tier flipped (e.g. KINGDOM_CLUB)
//   • first   — server-awarded (handled separately by the auth flow)

import { Events, emit, on } from '../ui/eventBus.js'
import { AchievementConfig, AchievementRarity, HolidayCalendar } from '../config/balance.js'
import { getProfile, active, persist } from './state.js'
// Module is imported back by accountManager (for awardServerFirst); that
// creates a benign cycle resolved by ES module bindings — getStreak is
// only invoked at runtime (inside isTriggered), after both modules finish
// initializing.
import { getStreak, getHolidaysSeen } from './accountManager.js'

// Game events the manager listens to and bumps a counter for. Add to the
// list to track new trigger types; the trigger config references the
// same event name as a string.
const TRACKED_EVENTS = [
  'WildGoonSlain', 'WildGoonConverted',
  'BuildingAdded',
  'MarriageAccepted', 'MarriageDivorced',
  'ChildBorn',
  'FeedActivated',
  'RaidStart', 'RaidEnd',
  'PlotChanged',
  'Click',
  'AdventureCompleted',
  'Harvest', // (a husband's household report counts his wives' harvests: profile.counters.Harvest)
]

export function init() {
  // Subscribe to each tracked event → bump counter → reevaluate triggers.
  for (const evName of TRACKED_EVENTS) {
    const evt = Events[evName]
    if (!evt) continue
    on(evt, () => {
      const p = ensureSchema()
      p.counters[evName] = (p.counters[evName] ?? 0) + 1
      persist()
      checkAll()
    })
  }
  // Field-watching triggers (kingdom.wealth, profile.openToMarriage, …) —
  // simpler to re-check on every state-change event than to subscribe to
  // each field individually.
  for (const evt of [
    Events.WealthChange,
    Events.ProfileChange,
    Events.MembershipChange,
    Events.OpenToMarriageChange,
    Events.PlotChanged,
    Events.BuildingAdded,
    Events.StreakChange,
    Events.HolidayParticipationStamp,
  ]) on(evt, checkAll)

  // First-time evaluation in case the profile already qualifies (e.g. after
  // a migration that introduces achievements to an existing save).
  checkAll()
}

function ensureSchema() {
  const p = getProfile()
  if (!p.counters || typeof p.counters !== 'object') p.counters = {}
  if (!p.achievements || typeof p.achievements !== 'object') p.achievements = {}
  return p
}

function isUnlocked(id) {
  return !!ensureSchema().achievements[id]
}

function isTriggered(def) {
  const p = ensureSchema()
  const k = active()
  const t = def.trigger ?? {}
  switch (t.kind) {
    case 'count': {
      const c = p.counters[t.event] ?? 0
      return c >= (t.threshold ?? Infinity)
    }
    case 'profile': {
      const v = p[t.field]
      if (typeof t.threshold === 'boolean') return v === t.threshold
      return (typeof v === 'number' ? v : 0) >= (t.threshold ?? Infinity)
    }
    case 'kingdom': {
      const v = k?.[t.field]
      return (typeof v === 'number' ? v : 0) >= (t.threshold ?? Infinity)
    }
    case 'activeMarriages': {
      const n = (p.marriages ?? []).filter((m) => m.status === 'ACTIVE').length
      return n >= (t.threshold ?? Infinity)
    }
    case 'membership': {
      return (p.membership ?? 'FREE') === (t.tier ?? 'KINGDOM_CLUB')
    }
    case 'streak': {
      const { current = 0 } = getStreak()
      return current >= (t.threshold ?? Infinity)
    }
    case 'adventureBiomes': {
      // Awarded when the player has completed ≥1 adventure in EVERY listed
      // biome. profile.adventureBiomesCompleted is stamped by adventureManager.
      const needed = t.biomes ?? []
      const seen = p.adventureBiomesCompleted ?? {}
      return needed.every((b) => !!seen[b])
    }
    case 'holiday': {
      // Awarded once a holidaysSeen token in the target family lands. The
      // server stamps tokens during the festival window via auth middleware.
      const seen = getHolidaysSeen() ?? {}
      for (const token of Object.keys(seen)) {
        const entry = HolidayCalendar.find((h) => h.token === token)
        if (entry?.family === t.family) return true
      }
      return false
    }
    case 'first': {
      // Server-awarded — comes in via the AchievementUnlocked event below,
      // never auto-evaluated here.
      return false
    }
    default:
      return false
  }
}

function checkAll() {
  const p = ensureSchema()
  for (const def of AchievementConfig) {
    if (p.achievements[def.id]) continue
    if (isTriggered(def)) award(def)
  }
}

export function award(def) {
  if (!def?.id) return
  const p = ensureSchema()
  if (p.achievements[def.id]) return // idempotent
  const awardedAt = Date.now()
  p.achievements[def.id] = { awardedAt }
  persist()
  const rarity = AchievementRarity[def.rarity] ?? AchievementRarity.common
  // Every unlock emits AchievementUnlocked — the corner toast subscribes.
  // Legendary unlocks ALSO get the full narrative-beat overlay — the toast
  // alone would be too quiet for a once-per-game trophy like Inaugural Member.
  emit(Events.AchievementUnlocked, { id: def.id, name: def.name, desc: def.desc, rarity: def.rarity, awardedAt })
  if (def.rarity === 'legendary') {
    emit(Events.NarrativeBeat, {
      title: `🏆 ${def.name} — ${rarity.label}`,
      body: def.desc,
      kingdom: null,
    })
  }
}

// Server-pushed "first to do X" trophies arrive via the account flow (e.g.
// the Stripe webhook flips a user to KINGDOM_CLUB and the response includes
// a `firsts: ['kingdom_club_member']` array). Call this when one lands.
export function awardServerFirst(token) {
  const def = AchievementConfig.find(
    (d) => d.trigger?.kind === 'first' && d.trigger.token === token,
  )
  if (def) award(def)
}

// Progress toward a locked achievement. Returns { current, target } for
// numeric triggers (count / kingdom / activeMarriages), or null for triggers
// without a meaningful in-progress signal (profile boolean flags, membership
// tier flip, server-awarded `first` trophies).
export function progressFor(def) {
  const p = ensureSchema()
  const k = active()
  const t = def?.trigger ?? {}
  switch (t.kind) {
    case 'count': {
      const target = t.threshold ?? 0
      if (!target) return null
      const current = Math.min(target, p.counters[t.event] ?? 0)
      return { current, target }
    }
    case 'kingdom': {
      const target = t.threshold ?? 0
      if (!target) return null
      const raw = k?.[t.field]
      const current = Math.min(target, typeof raw === 'number' ? raw : 0)
      return { current, target }
    }
    case 'activeMarriages': {
      const target = t.threshold ?? 0
      if (!target) return null
      const n = (p.marriages ?? []).filter((m) => m.status === 'ACTIVE').length
      return { current: Math.min(target, n), target }
    }
    case 'streak': {
      const target = t.threshold ?? 0
      if (!target) return null
      const { current = 0 } = getStreak()
      return { current: Math.min(target, current), target }
    }
    default:
      return null
  }
}

export function getUnlocked() {
  const p = ensureSchema()
  return Object.entries(p.achievements).map(([id, meta]) => {
    const def = AchievementConfig.find((d) => d.id === id)
    return def ? { ...def, awardedAt: meta.awardedAt } : null
  }).filter(Boolean).sort((a, b) => b.awardedAt - a.awardedAt)
}
